import React from 'react';
import { HighnoteTransactionEvent, ReturnedStatus } from 'API';
import { isTransactionFinalized } from 'app/services/agave/erpUtils';
import { TransactionStatusChip } from 'app/pages/overview/tabs/transactions/TransactionStatusChip';

// Only RETURNED overlays the review chip; RESUBMITTED falls through to it.
export const isReturned = (returnedStatus?: ReturnedStatus | null): boolean =>
  returnedStatus != null && returnedStatus === ReturnedStatus?.RETURNED;

// A finalized transaction's real status supersedes the returned overlay (also fixes
// rows already stuck showing "returned" after posting).
export const shouldShowReturnedStatus = (transaction?: HighnoteTransactionEvent | null): boolean =>
  isReturned(transaction?.returnedStatus) && !isTransactionFinalized(transaction);

type ReturnedStatusChipProps = {
  returnedStatus?: ReturnedStatus | null;
  // Red for the cardholder (their action is needed); gray for admins/reviewers.
  isCardholder?: boolean;
};

export const ReturnedStatusChip: React.FC<ReturnedStatusChipProps> = ({
  returnedStatus,
  isCardholder = false,
}) => {
  if (!isReturned(returnedStatus)) return null;
  return <TransactionStatusChip status="returned" isCardholder={isCardholder} />;
};

// Banner copy for returned/resubmitted, suppressed once the transaction is finalized.
export const getReturnedBannerKey = (
  transaction?: HighnoteTransactionEvent | null
): 'returned' | 'resubmitted' | null => {
  if (!transaction || isTransactionFinalized(transaction)) return null;
  const returnedStatus = transaction.returnedStatus;
  if (returnedStatus === ReturnedStatus?.RETURNED) return 'returned';
  if (returnedStatus === ReturnedStatus?.RESUBMITTED) return 'resubmitted';
  return null;
};
